import moment from "moment";
import React, {
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Button from "veneer/Button";
import { JollyRancherChip } from "veneer/Chip";
import DescriptionList, { DescriptionListItem } from "veneer/DescriptionList";
import PaperPopper from "veneer/PaperPopper";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import ToggleButtonGroup, { ToggleButton } from "veneer/ToggleButtonGroup";
import Typography from "veneer/Typography";

import {
  APP_ID_VSA,
  SYNTHETIC_APPLICATION_SOURCES,
} from "cortex/ui/src/constant/application";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { useFeatureFlag } from "cortex/ui/src/hooks";
import {
  getByApplicationName,
  icon,
  name,
} from "cortex/ui/src/redux-store/applications/selectors";
import { deviceService } from "cortex/ui/src/service/device";
import { Asset, AssetProps } from "cortex/ui/src/ui/asset";
import { TableDataSpinner } from "cortex/ui/src/ui/loader/TableDataSpinner";
import { ResponseLike, getErrorMessage } from "cortex/ui/src/util/response";
import { sortByProp } from "cortex/ui/src/util/sortByProp";

import UpcomingSlotsTimeline, {
  AssetFrame,
  SlotCard,
  TimelineChip,
  TimelineItem,
  TimelineScroller,
  TimelineTrack,
  UpcomingSlot,
} from "./UpcomingSlotsTimeline";

const BLACK_SCREEN_APP_ID = "__bs";
const BLACK_SCREEN_NAME = "Black Screen";
const HIDE_DELAY_MS = 150;

interface ApplicationViewChartProps {
  applications: Record<string, Application>;
  data: ReadonlyArray<ViewData>;
  deviceId?: string;
}

interface AssetData {
  appId: string;
  label: string;
  name: string;
  x: number | string;
  y: number;
}

interface HoveredPopoverState {
  anchorEl: HTMLElement | null;
  asset: AssetData | null;
}

interface UpcomingSlotsError {
  deviceId: string;
  message: string;
}

interface UpcomingSlotsResponse {
  slots?: {
    slots?: UpcomingSlot[];
  };
  environment?: string;
  fetchedAt: string;
  deviceId: string;
}

type ActiveViewType = "previous" | "upcoming";
type ViewData = [number | string, string, string?];

const PopperWrapper = styled(PaperPopper)(({ theme }) => ({
  zIndex: 1300,

  "& .MuiPaper-root": {
    borderRadius: theme.shape.borderRadius * 2,
  },
}));

const PopoverShell = styled(Box)({
  display: "inline-block",
});

const StatusImagesWrapper = styled(Box)(({ theme }) => ({
  direction: "rtl",
  overflowX: "auto",
  paddingBottom: theme.spacing(1.25),
  whiteSpace: "nowrap",

  "&::-webkit-scrollbar": {
    WebkitAppearance: "none",
  },

  "&::-webkit-scrollbar:vertical": {
    width: 11,
  },

  "&::-webkit-scrollbar:horizontal": {
    height: 11,
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    border: "2px solid white",
    borderRadius: 8,
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: "#fff",
    borderRadius: 8,
  },
}));

const StatusImageList = styled("ul")({
  listStyle: "none",
  margin: 0,
  padding: 0,
});

const StatusImageItem = styled("li")({
  display: "inline-block",
  marginRight: 12,
  verticalAlign: "middle",
});

const AssetPreview = styled(Asset)<AssetProps>(({ blackscreen }) =>
  blackscreen
    ? {
        height: 150,
        width: 30,
      }
    : {},
);

export default function ApplicationViewChart({
  applications,
  data,
  deviceId,
}: ApplicationViewChartProps): ReactElement {
  const IsUpcomingSlotsEnabled = useFeatureFlag("is-upcoming-slots-enabled");
  const [popover, setPopover] = useState<HoveredPopoverState>({
    anchorEl: null,
    asset: null,
  });
  const [isLoadingUpcomingSlots, setIsLoadingUpcomingSlots] = useState(false);
  const [upcomingSlotsError, setUpcomingSlotsError] =
    useState<UpcomingSlotsError | null>(null);
  const [upcomingSlotsResponse, setUpcomingSlotsResponse] =
    useState<UpcomingSlotsResponse | null>(null);
  const [upcomingSlotsToolbarAction, setUpcomingSlotsToolbarAction] =
    useState<ReactNode | null>(null);
  const [activeView, setActiveView] = useState<ActiveViewType>("previous");
  const currentDeviceIdRef = useRef(deviceId);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestUpcomingSlotsRequestRef = useRef(0);
  const previousDeviceIdRef = useRef(deviceId);

  currentDeviceIdRef.current = deviceId;

  const sortByXDesc = useMemo(
    () =>
      sortByProp.bind(null, "x", "desc") as (
        a: AssetData,
        b: AssetData,
      ) => number,
    [],
  );

  const clearHideTimeout = useCallback((): void => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const getAppName = useCallback(
    (appId: string): string => {
      if (appId === BLACK_SCREEN_APP_ID) {
        return BLACK_SCREEN_NAME;
      }
      const app = getByApplicationName(applications, appId);
      return app ? name(app) : appId;
    },
    [applications],
  );

  const hidePopover = useCallback((): void => {
    clearHideTimeout();

    hideTimeoutRef.current = setTimeout(() => {
      setPopover({
        anchorEl: null,
        asset: null,
      });
    }, HIDE_DELAY_MS);
  }, [clearHideTimeout]);

  const showPopover = useCallback(
    (asset: AssetData, event: MouseEvent<HTMLElement>): void => {
      clearHideTimeout();
      setPopover({
        anchorEl: event.currentTarget,
        asset,
      });
    },
    [clearHideTimeout],
  );

  const renderPopoverContent = useCallback(
    (assetData: AssetData): ReactNode => {
      if (assetData.name === BLACK_SCREEN_NAME) {
        return (
          <Typography variant="body2">
            Black screen is visible for only 1 second
          </Typography>
        );
      }

      const url = assetData.label ? assetData.label.toLowerCase() : "";

      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        return <Typography variant="body2">{assetData.label}</Typography>;
      }

      return (
        <a href={assetData.label} rel="noopener noreferrer" target="_blank">
          Download Asset
        </a>
      );
    },
    [],
  );

  const viewDataToAsset = useCallback(
    (viewData: ViewData): AssetData => {
      const [ts, appId, label = ""] = viewData;

      return {
        appId,
        label,
        name: getAppName(appId),
        x: ts,
        y: 1,
      };
    },
    [getAppName],
  );

  const assets = useMemo(
    () => data.map(viewDataToAsset).sort(sortByXDesc),
    [data, sortByXDesc, viewDataToAsset],
  );

  const canRequestUpcomingSlots = useMemo(() => {
    if (!deviceId || !IsUpcomingSlotsEnabled) {
      return false;
    }

    return Object.values(applications).some((app: any) => {
      return app?.name === APP_ID_VSA && (app.is_installed || app.is_own);
    });
  }, [applications, deviceId, IsUpcomingSlotsEnabled]);

  const loadUpcomingSlots = useCallback(async (): Promise<void> => {
    if (!deviceId || isLoadingUpcomingSlots) {
      return;
    }

    const requestedDeviceId = deviceId;
    const requestId = ++latestUpcomingSlotsRequestRef.current;
    setIsLoadingUpcomingSlots(true);
    setUpcomingSlotsError(null);

    try {
      const response = await deviceService.getUpcomingSlots(deviceId);

      const slots = response?.data?.data;

      const slotsObject = {
        slots,
        environment: response?.data?.environment,
        fetchedAt: new Date().toISOString(),
        deviceId,
      };

      if (
        currentDeviceIdRef.current === requestedDeviceId &&
        latestUpcomingSlotsRequestRef.current === requestId
      ) {
        setUpcomingSlotsResponse(slotsObject);
      }
    } catch (error) {
      if (
        currentDeviceIdRef.current === requestedDeviceId &&
        latestUpcomingSlotsRequestRef.current === requestId
      ) {
        setUpcomingSlotsError({
          deviceId: requestedDeviceId,
          message: getErrorMessage(error as Error | ResponseLike | null),
        });
      }
    } finally {
      if (
        currentDeviceIdRef.current === requestedDeviceId &&
        latestUpcomingSlotsRequestRef.current === requestId
      ) {
        setIsLoadingUpcomingSlots(false);
      }
    }
  }, [deviceId, isLoadingUpcomingSlots]);

  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  useEffect(() => {
    if (previousDeviceIdRef.current === deviceId) {
      return;
    }

    previousDeviceIdRef.current = deviceId;
    latestUpcomingSlotsRequestRef.current += 1;
    setActiveView("previous");
    setIsLoadingUpcomingSlots(false);
    setUpcomingSlotsResponse(null);
    setUpcomingSlotsToolbarAction(null);
  }, [deviceId]);

  const renderScheduledSlots = (): ReactNode => {
    if (isLoadingUpcomingSlots) {
      return <TableDataSpinner title="Loading scheduled slots…" />;
    }

    if (upcomingSlotsError && upcomingSlotsError.deviceId === deviceId) {
      return (
        <Alert
          dataTestId="scheduled-slots-error"
          dense
          severity="warning"
          title="Unable to load scheduled slots"
        >
          {upcomingSlotsError.message}
        </Alert>
      );
    }

    if (upcomingSlotsResponse) {
      return (
        <UpcomingSlotsTimeline
          environment={upcomingSlotsResponse.environment}
          onToolbarActionChange={setUpcomingSlotsToolbarAction}
          slots={upcomingSlotsResponse.slots?.slots ?? []}
        />
      );
    }

    return <Typography variant="body2">No scheduled slots data.</Typography>;
  };

  const renderPreviousSlotDetails = (assetData: AssetData): ReactNode => {
    const assetName =
      SYNTHETIC_APPLICATION_SOURCES[Number(assetData.appId)] ?? assetData.name;

    return (
      <DescriptionList>
        <DescriptionListItem label="Source">{assetName}</DescriptionListItem>
      </DescriptionList>
    );
  };

  const renderPreviousSlotAsset = (assetData: AssetData): ReactNode => {
    if (assetData.name === BLACK_SCREEN_NAME) {
      return (
        <Box p={2} pt={0}>
          <Typography variant="body2">
            Black screen is visible for only 1 second
          </Typography>
        </Box>
      );
    }

    const url = assetData.label ? assetData.label.toLowerCase() : "";
    const hasDownloadUrl =
      url.startsWith("http://") || url.startsWith("https://");

    if (!hasDownloadUrl) {
      if (!assetData.label) {
        return null;
      }

      return (
        <Box p={2} pt={0}>
          <Typography variant="body2">{assetData.label}</Typography>
        </Box>
      );
    }

    return (
      <Box p={2} pt={0}>
        <Button
          dataTestId="previous-slot-download-button"
          fullWidth
          href={assetData.label}
          icon="download"
          newTabLink
          variant="outlined"
        >
          Download Asset
        </Button>
      </Box>
    );
  };

  const renderPreviousSlots = (): ReactNode => {
    if (data.length === 0) {
      return <Typography variant="body2">No Data.</Typography>;
    }

    if (!IsUpcomingSlotsEnabled) {
      return (
        <StatusImagesWrapper>
          <StatusImageList>
            {assets.map((assetData, idx) => {
              const currentApp = getByApplicationName(
                applications,
                assetData.appId,
              );
              const appPlaceholder = currentApp ? icon(currentApp) : "";
              return (
                <StatusImageItem
                  key={`${assetData.name}-${assetData.x}-${idx}`}
                  onMouseEnter={(event: MouseEvent<HTMLElement>) => {
                    showPopover(assetData, event);
                  }}
                  onMouseLeave={hidePopover}
                >
                  <Box>
                    <AssetPreview
                      blackscreen={assetData.name === BLACK_SCREEN_NAME}
                      placeholder={appPlaceholder}
                      preload="none"
                      url={assetData.label}
                    />
                  </Box>
                </StatusImageItem>
              );
            })}
          </StatusImageList>
        </StatusImagesWrapper>
      );
    }

    return (
      <TimelineScroller data-testid="previous-slots-timeline">
        <TimelineTrack>
          {assets.map((assetData, idx) => {
            const currentApp = getByApplicationName(
              applications,
              assetData.appId,
            );
            const appPlaceholder = currentApp ? icon(currentApp) : "";
            const formattedDate = moment
              .utc(assetData.x)
              .local()
              .format("MMM Do, YYYY hh:mm:ss a");

            return (
              <TimelineItem key={`${assetData.name}-${assetData.x}-${idx}`}>
                <TimelineChip>
                  <JollyRancherChip color="blue" label={formattedDate} />
                </TimelineChip>
                <SlotCard>
                  <AssetFrame>
                    <AssetPreview
                      blackscreen={assetData.name === BLACK_SCREEN_NAME}
                      placeholder={appPlaceholder}
                      preload="none"
                      url={assetData.label}
                    />
                  </AssetFrame>
                  {renderPreviousSlotDetails(assetData)}
                  {renderPreviousSlotAsset(assetData)}
                </SlotCard>
              </TimelineItem>
            );
          })}
        </TimelineTrack>
      </TimelineScroller>
    );
  };

  return (
    <>
      {canRequestUpcomingSlots && (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          mb={2}
        >
          <ToggleButtonGroup
            onChange={(value?: string) => {
              if (value !== "previous" && value !== "upcoming") {
                return;
              }

              setActiveView(value);

              if (value === "upcoming") {
                loadUpcomingSlots();
              }
            }}
          >
            <ToggleButton selected={activeView === "previous"} value="previous">
              Previous Slots
            </ToggleButton>
            <ToggleButton selected={activeView === "upcoming"} value="upcoming">
              Scheduled Slots
            </ToggleButton>
          </ToggleButtonGroup>
          {activeView === "upcoming" && upcomingSlotsToolbarAction}
        </Box>
      )}
      {activeView === "previous" && renderPreviousSlots()}
      {activeView === "upcoming" && <Box>{renderScheduledSlots()}</Box>}
      {!IsUpcomingSlotsEnabled && (
        <PopoverShell
          onMouseEnter={clearHideTimeout}
          onMouseLeave={hidePopover}
        >
          <PopperWrapper
            anchorEl={popover.anchorEl}
            dataTestId="application-view-chart-asset-popover"
            elevation={4}
            open={Boolean(popover.anchorEl && popover.asset)}
            placement="top"
          >
            {popover.asset ? (
              <Stack p={2} spacing={1}>
                <Typography variant="h6">{popover.asset.name}</Typography>
                {renderPopoverContent(popover.asset)}
                <Typography variant="body2">
                  {moment
                    .utc(popover.asset.x)
                    .local()
                    .format("MMMM Do YYYY, h:mm:ss a")}
                </Typography>
              </Stack>
            ) : null}
          </PopperWrapper>
        </PopoverShell>
      )}
    </>
  );
}
